/// <amd-module name="TFS/Admin/Groups/Dialogs/GroupAddPanel/GroupAddPanel" />

import * as React from "react";
import * as Resources from "TFS/Admin/Groups/Common/Resources";

import { AvatarSize } from "TFS/Admin/Common/Contracts/VSS.Profile";
import { GroupNameMaxChar, GroupDescriptionMaxChar, IReservedGroupNames, TeamsMinAdminNumber, ContributorsWellKnownGroup, IEntityType } from "TFS/Admin/Groups/Common/Constants";
import { IIdentity, IPeoplePickerProvider, IdentityPicker, IdentityType } from "VSSUI/IdentityPicker";
import { ImageData, InputFileSelect } from "TFS/Admin/Common/Contracts/AvatarImagePicker.types";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";

import { ContentSize } from "VSSUI/Callout";
import { CustomPeoplePickerProvider } from "TFS/Admin/Common/Providers/CustomPeoplePickerProvider";
import { FormItem } from "VSSUI/FormItem";
import { GroupAddPanelProps } from "TFS/Admin/Groups/Dialogs/GroupAddPanel/GroupAddPanel.Props";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { filterXSS } from "xss";
import { IButtonProps } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Panel } from "VSSUI/Panel";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { TeamAddSection } from "TFS/Admin/Groups/Dialogs/GroupAddPanel/TeamAddSection";
import { TextField } from "VSSUI/TextField";
import { VssPersona } from "VSSUI/VssPersona";
import { getAssetLocation } from "VSS/Platform/Location";
import { localeFormat } from "VSS/Core/Util/String";
import { validateEmail } from "TFS/Admin/Common/Util/EmailValidator";
import { caseInsensitiveContains } from "VSS/Core/Util/String";
import { KeyCode } from "VSSUI/Util";

interface GroupAddPanelState {
    groupDisplayName: string;
    groupDescription: string;
    isSaving: boolean;
    showChangeImageDialog: boolean;
    groupAvatar: number[];
    deleteAvatar: boolean;
    currentGroupAvatarUrl: string;
    invalidMembers: IIdentity[];
    validMembers: IIdentity[];
    adminsSize: number;
}

const setAreaPath = new ObservableValue<boolean>(true);

export class GroupAddPanel extends React.Component<GroupAddPanelProps, GroupAddPanelState> {
    private static readonly _imageUrlPrefix: string = "data:image/png;base64,";
    private _pickerProvider: IPeoplePickerProvider;
    private newGroupNameErrorMessage = new ObservableValue<string>("");
    private _adminSelectedIdentities = new ObservableArray<IIdentity>([]);
    private _parentSelectedIdentity = new ObservableValue<IIdentity | undefined>(undefined);
    private _adminAsMember = new ObservableValue<boolean>(true);

    constructor(props: GroupAddPanelProps) {
        super(props);

        if (!this.props.isAadOrg) {
            this._pickerProvider = new CustomPeoplePickerProvider(this.props.pageContext, [IdentityType.Group, IdentityType.User], []);
        } else {
            this._pickerProvider = new PeoplePickerProvider(this.props.pageContext, {
                identityTypes: this.props.supportServicePrincipals ? [IEntityType.User, IEntityType.Group, IEntityType.ServicePrincipal] : [IEntityType.User, IEntityType.Group],
                options: {
                    MinResults: 5,
                    MaxResults: 20
                }
            });
        }

        this.state = {
            groupDisplayName: "",
            groupDescription: "",
            isSaving: false,
            showChangeImageDialog: false,
            groupAvatar: [],
            currentGroupAvatarUrl: "",
            deleteAvatar: false,
            validMembers: [],
            invalidMembers: [],
            adminsSize: TeamsMinAdminNumber
        };

        if (this.props.isTeamsPage && this.props.permissionsGroups) {
            const contributorsGroup: IIdentity = this.props.permissionsGroups.filter(x =>
                caseInsensitiveContains(x.displayName, ContributorsWellKnownGroup)
            )[0];

            if (contributorsGroup) {
                this._parentSelectedIdentity = new ObservableValue<IIdentity>(contributorsGroup);
            }
        }
    }

    public render(): JSX.Element {
        const footerButtons: IButtonProps[] = [
            {
                disabled: false,
                onClick: this.props.onDismiss,
                text: Resources.Cancel
            },
            {
                className: "primary-action-button",
                disabled: this._saveButtonDisabled(),
                onClick: this._onSave,
                primary: true,
                text: Resources.CreateGroupButton
            }
        ];

        return (
            <>
                <Panel
                    size={ContentSize.Medium}
                    onDismiss={this.props.onDismiss}
                    titleProps={{ text: localeFormat(Resources.GroupAddPanelName, this._getType().toLowerCase()) }}
                    footerButtonProps={footerButtons}
                    defaultActiveElement=".group-add-item input"
                >
                    {this._getGroupAddPanelContent()}
                </Panel>
            </>
        );
    }

    private _getGroupAddPanelContent(): JSX.Element | null {
        let imageUrl = this.state.currentGroupAvatarUrl || getAssetLocation(this.props.pageContext, "ms.vss-admin-web/admin-common-content/team.svg");
        let loadingImage = this.state.currentGroupAvatarUrl === undefined;

        return (
            <div className="permissions-groups-add-wrapper flex-column rhythm-vertical-20">
                {this.state.showChangeImageDialog ? (
                    <WrappedComponent
                        dependencies={["ms.vss-features.ui-avatar-image-picker-content"]}
                        wrappedType={"vss.avatar-image-picker"}
                        wrappedProps={{
                            onCancel: (hasChanges: boolean) => {
                                this._cancelImageUpload(hasChanges);
                            },
                            onSave: (resetImage: boolean, imageData: ImageData) => {
                                this._saveImageAction(resetImage, imageData);
                            },
                            onFileSelected: (error: string, imageData: ImageData) => {
                                return this._onImageFileSelected(error, imageData);
                            },
                            onLoadDisplayAvatarUrl: imageUrl,
                            title: localeFormat(Resources.GroupAvatarImage, this._getType()),
                            defaultResetAvatarUrl: getAssetLocation(this.props.pageContext, "ms.vss-admin-web/admin-common-content/team.svg")
                        }}
                    />
                ) : null}

                <div>
                    {!loadingImage ? (
                        <div
                            className={
                                "persona-list-element profile-card-tab-element image-container " + (this.state.isSaving ? "disabled" : "enabled")
                            }
                            aria-label={Resources.UploadImage}
                            onClick={() => {
                                this._onChangeImageClick();
                            }}
                            data-is-focusable={true}
                            role="button"
                            tabIndex={0}
                            onKeyDown={ev => this.onKeyDown(ev)}
                        >
                            <VssPersona
                                identityDetailsProvider={{
                                    getIdentityImageUrl: (size: number): string | undefined => {
                                        return imageUrl;
                                    },
                                    getDisplayName: (): string => {
                                        return "";
                                    }
                                }}
                                suppressPersonaCard={true}
                                imgAltText={Resources.GroupProfileImage}
                                size="extra-large"
                                cssClass="project-persona"
                            />
                            <div className="overlay" />
                            <Icon iconName="Edit" className="ms-font-l editIcon" />
                        </div>
                    ) : (
                        <div className="image-container flex flex-column flex-center justify-center">
                            <span>
                                <Spinner size={SpinnerSize.large} className="imageSpinner" />
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex-column rhythm-vertical-8">
                    <FormItem
                        className="group-add-item"
                        label={Resources.GroupName}
                        message={this._getGroupNameErrorMessage()}
                        error={this.newGroupNameErrorMessage.value !== "" || this.props.groupNameAlreadyExists}
                    >
                        <TextField
                            disabled={this.state.isSaving}
                            value={this.state.groupDisplayName}
                            onChange={this._onGroupNameEdit}
                            placeholder={localeFormat(Resources.GroupNamePlaceholder, this._getType())}
                        />
                    </FormItem>
                </div>
                <div className="flex-column">
                    <FormItem
                        className="group-add-item"
                        label={Resources.GroupMembership}
                        message={this._getErrorMessage()}
                        error={this.state.invalidMembers.length > 0}
                    >
                        <IdentityPicker
                            pickerProvider={this._pickerProvider}
                            onIdentityAdded={this._onIdentityAdded}
                            onIdentitiesRemoved={this._onIdentitiesRemoved}
                            onIdentityRemoved={this._onIdentityRemoved}
                            selectedIdentities={this.state.validMembers}
                            placeholderText={Resources.MemberAddIdentityPickerPlaceholder}
                            onResolveEntity={this._onResolveEmail}
                        />
                    </FormItem>
                    {!this.props.isTeamsPage && <FormItem label={this.props.supportServicePrincipals ? Resources.GroupAddExplanatoryLabelWithSPs : Resources.GroupAddExplanatoryLabel} />}
                </div>
                <FormItem label={Resources.Description}>
                    <TextField
                        multiline={true}
                        disabled={this.state.isSaving}
                        value={this.state.groupDescription}
                        onChange={this._onGroupDescriptionEdit}
                        maxLength={GroupDescriptionMaxChar}
                        rows={5}
                        placeholder={localeFormat(Resources.GroupDescriptionPlaceholder, this._getType().toLowerCase())}
                    />
                </FormItem>
                {this.props.isTeamsPage && (
                    <TeamAddSection
                        pageContext={this.props.pageContext}
                        onAdminAdded={this._onAdminAdded}
                        onAdminsRemoved={this._onAdminsRemoved}
                        onAdminRemoved={this._onAdminRemoved}
                        parentSelectedIdentity={this._parentSelectedIdentity}
                        adminSelectedIdentities={this._adminSelectedIdentities}
                        setAreaPath={setAreaPath}
                        onAreaPathCheckChanged={this._onAreaPathCheckChanged}
                        adminAsMember={this._adminAsMember}
                        onAdminAsMemberChanged={this._onAdminAsMemberChanged}
                        permissionsGroups={this.props.permissionsGroups}
                    />
                )}

                {this.state.isSaving ? (
                    <div className="flex flex-column flex-center justify-center overlay-spinner">
                        <Spinner size={SpinnerSize.large} label={Resources.SavingSpinnerLabel} />
                    </div>
                ) : null}
            </div>
        );
    }

    private onKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
        switch (ev.which) {
            case KeyCode.enter:
            case KeyCode.space:
                this._onChangeImageClick();
                break;
            default:
                return;
        }

        ev.preventDefault();
        ev.stopPropagation();
    };

    private _getGroupNameErrorMessage() {
        if (this.newGroupNameErrorMessage.value != "") {
            return this.newGroupNameErrorMessage.value;
        }
        if (this.props.groupNameAlreadyExists) {
            return Resources.GroupNameAlreadyExists;
        }
    }

    private _onAreaPathCheckChanged(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) {
        setAreaPath.value = checked;
    }

    private _onAdminAsMemberChanged = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => {
        this._adminAsMember.value = checked;
    }

    private _onResolveEmail = (email: string, identity: IIdentity | null): void => {
        const { validMembers, invalidMembers } = this.state;
        const index = validMembers.findIndex(x => x.originDirectory == "email" && x.entityId == email);
        if (index === -1) return;
        validMembers.splice.apply(validMembers, identity ? [index, 1, identity] : [index, 1]);
        invalidMembers.splice(
            invalidMembers.findIndex(x => x.originDirectory == "email" && x.entityId == email),
            1
        );
        this.props.onLogTelemetry(GroupsComponent.GroupsPivot, `${this._getPanelIdentifier()} Member email resolved to identity`);
        this.setState({ validMembers, invalidMembers });
    };

    private _onIdentityAdded = (item: IIdentity): void => {
        const { validMembers, invalidMembers } = this.state;
        if (validMembers.findIndex(x => x.entityId === item.entityId) > -1) return;
        if ((item.originDirectory === IdentityType.Custom && !validateEmail(item.entityId)) || item.originDirectory == "email") {
            this.setState({ validMembers: validMembers.concat(item), invalidMembers: invalidMembers.concat(item) });
        } else {
            this.setState({ validMembers: validMembers.concat(item) });
        }
    };

    private _onIdentitiesRemoved = (items: IIdentity[]): void => {
        this.setState({
            validMembers: this.state.validMembers.filter(entity => items.filter(item => item.entityId === entity.entityId).length === 0),
            invalidMembers: this.state.invalidMembers.filter(entity => items.filter(item => item.entityId === entity.entityId).length === 0)
        });
    };

    private _onIdentityRemoved = (item: IIdentity): void => {
        this.setState({
            validMembers: this.state.validMembers.filter(entity => entity.entityId !== item.entityId),
            invalidMembers: this.state.invalidMembers.filter(entity => entity.entityId !== item.entityId)
        });
    };

    private _onSave = () => {
        this.setState({ isSaving: true });

        this.props.onSave(
            filterXSS(this.state.groupDisplayName),
            filterXSS(this.state.groupDescription),
            this.state.validMembers,
            this._adminSelectedIdentities.value,
            this._adminAsMember.value,
            setAreaPath.value,
            this._parentSelectedIdentity.value,
            {
                isAutoGenerated: false,
                size: AvatarSize.Large,
                timeStamp: new Date(),
                value: this.state.groupAvatar
            }
        );
    };

    private _validateGroupName(groupName: string) {
        groupName = groupName.trim();

        if (groupName === "") {
            this.newGroupNameErrorMessage.value = localeFormat(Resources.EmptyGroupNameError, this._getType());
        } else if (groupName.length > GroupNameMaxChar) {
            this.newGroupNameErrorMessage.value = localeFormat(Resources.GroupNameTooLargeError, this._getType(), GroupNameMaxChar);
        } else if (groupName.endsWith(".")) {
            this.newGroupNameErrorMessage.value = localeFormat(Resources.GroupNameEndingInPeriodError, this._getType());
        } else if (!!/[,"/\\[\]:|<>+=;?*@~'&\$#]/g.test(groupName)) {
            this.newGroupNameErrorMessage.value = localeFormat(Resources.IllegalCharactersNameError, this._getType());
        } else if (!!/[^ -~]+/g.test(groupName)) {
            this.newGroupNameErrorMessage.value = localeFormat(Resources.NonPrintableCharactersNameError, this._getType());
        } else if (
            groupName.toLowerCase() == IReservedGroupNames.Global.toLowerCase() ||
            groupName.toLowerCase() == IReservedGroupNames.AdministratorsGroup.toLowerCase() ||
            groupName.toLowerCase() == IReservedGroupNames.ServiceUsersGroup.toLowerCase() ||
            groupName.toLowerCase() == IReservedGroupNames.EveryoneGroup.toLowerCase()
        ) {
            this.newGroupNameErrorMessage.value = localeFormat(Resources.ReservedGroupNameError, this._getType().toLowerCase());
        } else {
            this.newGroupNameErrorMessage.value = "";
        }
    }

    private _onGroupNameEdit = (event: any, groupDisplayName: string) => {
        this._validateGroupName(groupDisplayName);
        this.setState({ groupDisplayName });
        if (this.newGroupNameErrorMessage.value == "") {
            this.props.checkGroupExistence(groupDisplayName);
        }
    };

    private _onGroupDescriptionEdit = (event: any, groupDescription: string) => {
        this.setState({ groupDescription });
    };

    private _saveButtonDisabled(): boolean {
        if (this.newGroupNameErrorMessage.value !== "") {
            return true;
        }

        if (this.state.invalidMembers.length > 0) {
            return true;
        }

        if (this.props.isTeamsPage && this.state.adminsSize < TeamsMinAdminNumber) {
            return true;
        }

        if (this.props.groupNameAlreadyExists) {
            return true;
        }

        const { state } = this;
        return !state.groupDisplayName || (state.isSaving && state.groupAvatar.length === 0 && !state.deleteAvatar);
    }

    private _onChangeImageClick = (): void => {
        this.props.onLogTelemetry(GroupsComponent.GroupsPivot, `${this._getPanelIdentifier()} ImageDialog open`);
        this.setState({ showChangeImageDialog: true });
    };

    private _cancelImageUpload = (hasChanges: boolean) => {
        this.props.onLogTelemetry(GroupsComponent.GroupsPivot, `${this._getPanelIdentifier()} ImageDialog cancel`);
        this.setState({ deleteAvatar: false, groupAvatar: [], showChangeImageDialog: false });
    };

    private async _saveImageAction(resetImage: boolean, imageData: ImageData) {
        this.props.onLogTelemetry(GroupsComponent.GroupsPivot, `${this._getPanelIdentifier()} ImageDialog ${resetImage ? "Reset" : "Save"} image`);
        this.setState({
            deleteAvatar: resetImage,
            showChangeImageDialog: false,
            groupAvatar: imageData.byteArray,
            currentGroupAvatarUrl: imageData.url
        });
    }

    private _onImageFileSelected(error: string, imageData: ImageData): InputFileSelect {
        const result = {} as InputFileSelect;

        // File validation error from image select.
        result.previewImage = !error;
        result.error = error || "";

        return result;
    }

    private _getPanelIdentifier(): string {
        return `${this._getType()}AddPanel`;
    }

    private _getErrorMessage = (): string => {
        if (this.state.invalidMembers.length > 0) {
            let wrongEmailIdentitiesStr: string = this.state.invalidMembers.map(e => e.displayName).join(", ");
            return localeFormat(Resources.ErrorInMemberEmail, filterXSS(wrongEmailIdentitiesStr));
        }

        return "";
    };

    private _getType(): string {
        return this.props.isTeamsPage ? Resources.Team : Resources.Group;
    }

    private _onAdminsRemoved = (identities: IIdentity[]) => {
        this._adminSelectedIdentities.value = this._adminSelectedIdentities.value.filter(
            (entity: IIdentity) => identities.filter(item => item.entityId === entity.entityId).length === 0
        );
        this.setState({ adminsSize: this._adminSelectedIdentities.length });
    };

    private _onAdminAdded = (identity: IIdentity) => {
        this._adminSelectedIdentities.push(identity);
        this.setState({ adminsSize: this._adminSelectedIdentities.length });
    };

    private _onAdminRemoved = (identity: IIdentity) => {
        this._adminSelectedIdentities.value = this._adminSelectedIdentities.value.filter(
            (entity: IIdentity) => entity.entityId !== identity.entityId
        );
        this.setState({ adminsSize: this._adminSelectedIdentities.length });
    };
}

VssComponent.register("ms.vss-admin-web.orggroups-group-add-panel", GroupAddPanel);
